import { useState } from "react";
import { Checkbox, Input } from "antd";
import { Check } from "lucide-react";
import styled from "styled-components";
import type { RoleRegisterSession } from "../../utils/roleRegisterSession";
import { RoleRegisterActions } from "./RoleRegisterActions";
import PasswordInputWithValidation from "../common/PasswordInputWithValidation";
import { PasswordRequirements } from "../common/PasswordRequirements";
import { FormField } from "@/shared/components";
import { describeMissingPasswordRequirements } from "@/shared/utils/passwordValidation";
import googleIconUrl from "@/assets/icons/google.png?url";

const DetailsStep = styled.div`
	display: contents;
`;

const FormBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;

	[role="alert"] {
		position: static;
		margin-top: 2px;
	}
`;

const RoleBanner = styled.div`
	height: 95px;
	padding: 6px 12px;
	border-radius: 8px;
	background: rgba(27, 2, 85, 0.04);
	display: flex;
	align-items: center;
	justify-content: space-between;
	overflow: hidden;
`;

const RoleCopy = styled.div`
	h2 {
		margin: 0;
		font-size: 24px;
		font-weight: 600;
		line-height: normal;
		color: #0b0a0c;
	}

	strong {
		color: #3a0ca3;
		font-weight: 600;
	}

	p {
		margin: 0;
		font-size: 14px;
		line-height: 24px;
		color: #707070;
	}
`;

const RoleIllustration = styled.img`
	width: 129px;
	height: 98px;
	object-fit: contain;
	object-position: top center;
	transform-origin: top center;
`;

const StyledInput = styled(Input)`
	height: 40px;
	border-radius: 8px;
	border-color: #cdd4f6;
	font-size: 16px;

	&:hover,
	&:focus,
	&.ant-input-status-error {
		border-color: #3a0ca3;
	}
`;

const StyledPasswordInput = styled(PasswordInputWithValidation)`
	.ant-input-affix-wrapper {
		height: 40px;
		border-radius: 8px;
		border-color: #cdd4f6;
	}
`;

const LegalCheckbox = styled(Checkbox)`
	a,
	button {
		border: 0;
		padding: 0;
		background: transparent;
		color: #3a0ca3;
		font: inherit;
		font-weight: 600;
		text-decoration: underline;
		cursor: pointer;
	}

	margin-top: 8px;
`;

const RequiredMark = styled.span`
	color: #d80027;
`;

const GoogleButton = styled.button`
	width: 100%;
	height: 47px;
	border: 1px solid #d9d9d9;
	border-radius: 50px;
	background: #fff;
	box-shadow: 0 2px 0 rgba(0, 0, 0, 0.02);
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	font: inherit;
	font-size: 14px;
	color: rgba(34, 34, 34, 0.87);
	cursor: pointer;

	&:hover {
		border-color: #3a0ca3;
	}
`;

const GoogleIcon = styled.img`
	width: 32px;
	height: 32px;
	object-fit: contain;
`;

const GoogleLinked = styled.div`
	border: 1px solid #66b026;
	border-radius: 12px;
	padding: 12px;
	background: #f1faef;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const GoogleLinkedIdentity = styled.div`
	display: flex;
	align-items: center;
	flex: 0 1 auto;
	min-width: 0;
	gap: 13px;
`;

const GoogleStatusIcon = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 24px;
	width: 24px;
	height: 24px;
	padding: 4px;
	border-radius: 16px;
	background: #66b026;
	color: #fff;
	box-sizing: border-box;

	svg {
		display: block;
		width: 16px;
		height: 16px;
	}
`;

const LinkedCopy = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	flex: 0 1 197px;
	width: 197px;
	min-width: 0;
	white-space: nowrap;

	strong,
	span {
		display: block;
		width: 100%;
		overflow: hidden;
		text-overflow: ellipsis;
		line-height: 24px;
	}

	strong {
		font-size: 14px;
		font-weight: 600;
		color: #509018;
	}

	span {
		font-size: 14px;
		font-weight: 400;
		color: #303030;
	}
`;

const ChangeButton = styled.button`
	display: block;
	flex: 0 0 70px;
	width: 70px;
	border: 0;
	padding: 0;
	background: transparent;
	color: #3a0ca3;
	font: inherit;
	font-size: 14px;
	font-weight: 600;
	line-height: 24px;
	text-align: center;
	cursor: pointer;
`;

interface RoleRegisterDetailsStepProps {
	role: "creator" | "client" | "agency";
	session: RoleRegisterSession;
	emailError: string;
	agencyEmailError?: string;
	canSubmit: boolean;
	loading: boolean;
	onSessionChange: (partial: Partial<RoleRegisterSession>) => void;
	onCheckEmail: () => void;
	onOpenTerms: () => void;
	onOpenPrivacy: () => void;
	onSubmit: () => void;
	onGoogleSignup: () => void;
	onChangeGoogleAccount: () => void;
	emailLocked?: boolean;
	allowGoogleSignup?: boolean;
	password: string;
	confirmPassword: string;
	passwordError?: string;
	onPasswordChange: (password: string) => void;
	onConfirmPasswordChange: (password: string) => void;
}

export const RoleRegisterDetailsStep = ({
	role,
	session,
	emailError,
	agencyEmailError,
	canSubmit,
	loading,
	onSessionChange,
	onCheckEmail,
	onOpenTerms,
	onOpenPrivacy,
	onSubmit,
	onGoogleSignup,
	onChangeGoogleAccount,
	emailLocked = false,
	allowGoogleSignup = true,
	password,
	confirmPassword,
	passwordError,
	onPasswordChange,
	onConfirmPasswordChange,
}: RoleRegisterDetailsStepProps) => {
	// Name the missing rules once the user has left the field, not on the first keystroke.
	const [passwordTouched, setPasswordTouched] = useState(false);
	const passwordRulesError = passwordTouched && password ? describeMissingPasswordRequirements(password) : null;

	return (
		<DetailsStep>
			<FormBody>
				<RoleBanner>
					<RoleCopy>
						<h2>
							Sign up as {role === "agency" ? "an" : "a"}{" "}
							<strong>{role === "agency" ? "Agency" : role === "client" ? "Client" : "Creator"}</strong>
						</h2>
						<p>Set up your Creatorverse account</p>
					</RoleCopy>
					<RoleIllustration
						src={`/assets/creator-register/${role === "agency" ? "agency" : role === "client" ? "client" : "creator"}-role.svg`}
						alt=""
					/>
				</RoleBanner>

				{role === "creator" && (
					<>
						<FormField label="Legal First Name" htmlFor="creator-register-first-name" required>
							<StyledInput
								id="creator-register-first-name"
								autoComplete="given-name"
								value={session.firstName}
								required
								aria-required="true"
								onChange={(event) => onSessionChange({ firstName: event.target.value })}
							/>
						</FormField>

						<FormField label="Legal Last Name" htmlFor="creator-register-last-name" required>
							<StyledInput
								id="creator-register-last-name"
								autoComplete="family-name"
								value={session.lastName}
								required
								aria-required="true"
								onChange={(event) => onSessionChange({ lastName: event.target.value })}
							/>
						</FormField>
					</>
				)}

				<FormField
					label="Email"
					htmlFor="creator-register-email"
					error={agencyEmailError || emailError}
					required
				>
					<StyledInput
						id="creator-register-email"
						type="email"
						autoComplete="email"
						value={session.email}
						disabled={Boolean(session.google) || emailLocked}
						required
						aria-required="true"
						status={agencyEmailError || emailError ? "error" : undefined}
						onChange={(event) => onSessionChange({ email: event.target.value, emailVerified: false })}
						onBlur={onCheckEmail}
					/>
				</FormField>

				{!session.google && (
					<>
						<FormField
							label="Password"
							htmlFor="creator-register-password"
							error={passwordRulesError}
							required
						>
							<StyledPasswordInput
								id="creator-register-password"
								value={password}
								onChange={(event) => onPasswordChange(event.target.value)}
								onBlur={() => setPasswordTouched(true)}
								status={passwordRulesError ? "error" : undefined}
								aria-describedby="creator-register-password-requirements"
								placeholder=""
								prefix={null}
								showValidation={false}
							/>
						</FormField>
						<PasswordRequirements id="creator-register-password-requirements" password={password} />
						<FormField
							label="Confirm Password"
							htmlFor="creator-register-confirm-password"
							error={passwordError}
							required
						>
							<StyledPasswordInput
								id="creator-register-confirm-password"
								value={confirmPassword}
								onChange={(event) => onConfirmPasswordChange(event.target.value)}
								placeholder=""
								prefix={null}
								showValidation={false}
							/>
						</FormField>
					</>
				)}

				<LegalCheckbox
					checked={session.accepted}
					required
					aria-required="true"
					onChange={(event) => onSessionChange({ accepted: event.target.checked })}
					aria-label="Accept Terms of Service and Privacy Policy"
				>
					I agree to the{" "}
					<button
						type="button"
						onClick={(event) => {
							event.preventDefault();
							event.stopPropagation();
							onOpenTerms();
						}}
					>
						Terms of Service
					</button>{" "}
					&amp;{" "}
					<button
						type="button"
						onClick={(event) => {
							event.preventDefault();
							event.stopPropagation();
							onOpenPrivacy();
						}}
					>
						Privacy Policy.
					</button>
					<RequiredMark aria-hidden="true"> *</RequiredMark>
				</LegalCheckbox>

				{session.google && (
					<GoogleLinked>
						<GoogleLinkedIdentity>
							<GoogleStatusIcon aria-hidden>
								<Check strokeWidth={2} />
							</GoogleStatusIcon>
							<LinkedCopy>
								<strong>Signed up with Google</strong>
								<span>{session.google.email}</span>
							</LinkedCopy>
						</GoogleLinkedIdentity>
						<ChangeButton type="button" onClick={onChangeGoogleAccount}>
							Change
						</ChangeButton>
					</GoogleLinked>
				)}
			</FormBody>

			<RoleRegisterActions
				primaryLabel="Create Account"
				primaryDisabled={!canSubmit || loading}
				onPrimaryClick={onSubmit}
				compact
			>
				{!session.google && allowGoogleSignup && (
					<GoogleButton type="button" onClick={onGoogleSignup}>
						<GoogleIcon src={googleIconUrl} alt="" />
						Sign up with Google
					</GoogleButton>
				)}
			</RoleRegisterActions>
		</DetailsStep>
	);
};
